import { clsx } from '@alexis/helpers/clsx';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { convertDurationInMinutesToHoursAndMinutes } from '@helpers/utils';

import { getIsRtlState, getTranslationsState } from '@redux/selectors';

import type { SegmentViewModel } from '@wegoTypes/flights/segmentViewModel';

import styles from './NewSegmentView.module.scss';

export interface NewSegmentView {
  segmentViewModels: SegmentViewModel[];
}

const NewSegmentView: React.FC<NewSegmentView> = ({ segmentViewModels }) => {
  const isRtl = useSelector(getIsRtlState);
  const translations = useSelector(getTranslationsState);

  const stopOverTimeTooltipText = useMemo(() => {
    return segmentViewModels.map((segmentViewModel) => {
      const stopOverTimeText =
        convertDurationInMinutesToHoursAndMinutes(
          segmentViewModel.segment.stopoverDurationMinutes,
        ) + ' ';

      if (isRtl)
        return (
          <div key={segmentViewModel.segment.arrivalAirport?.name} className={styles.tooltipItem}>
            <span>{translations.stopover}</span>
            <span data-testid='stopover-duration'>{stopOverTimeText}</span>
            <br />
            {segmentViewModel.segment.arrivalAirport?.name}
          </div>
        );

      return (
        <div key={segmentViewModel.segment.arrivalAirport?.name} className={styles.tooltipItem}>
          <span data-testid='stopover-duration'>{stopOverTimeText}</span>
          <span>{(translations.stopover as string)?.toLowerCase()}</span>
          <br />
          {segmentViewModel.segment.arrivalAirport?.name}
        </div>
      );
    });
  }, [isRtl, segmentViewModels, translations.stopover]);

  const segmentViewModel = segmentViewModels[0];

  const isMultipleSegmentView = segmentViewModels.length > 1;

  return (
    <div className={styles.container}>
      <div
        data-pw='leg_stopoverInfo'
        data-testid='stopover'
        className={styles.tooltipContainer}
        style={{
          [isRtl ? 'right' : 'left']: `${segmentViewModel.pointStartPercentage}%`,
          width: `${segmentViewModel.pointWidthPercentage}%`,
        }}
      >
        <div className={styles.point} style={{ [isRtl ? 'right' : 'left']: `0` }}></div>

        <div
          className={styles.code}
          style={{ [isRtl ? 'right' : 'left']: `50%` }}
          data-testid='stopover-airport'
        >
          {isMultipleSegmentView
            ? `+${segmentViewModels.length}`
            : segmentViewModel.segment.arrivalAirportCode}
        </div>

        <div
          className={clsx(styles.tooltip, styles.bottom, isMultipleSegmentView && styles.multiple)}
          role='tooltip'
        >
          {stopOverTimeTooltipText}
        </div>
      </div>
    </div>
  );
};

export default NewSegmentView;
